import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Tooltip from "components/atoms/Tooltip";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TableCell = styled.td`
    z-index: 0;
`;

const TableCellWrap = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: start;
    width: 100%;
    padding-left: 4px;
`;

const TableCellTextBase = styled.div`
    overflow: hidden;
    white-space: normal;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-weight: 400;
    line-height: 1.5;
    letter-spacing: -0.03em;
    font-size: 1.4rem;
    word-break: break-all;
`;

const TableCellTextClickable = styled(TableCellTextBase)`
    cursor: pointer;

    &:hover {
        font-weight: 500;
        text-decoration: underline;
    }
`;

const TableCellContent = styled.div`
    overflow: hidden;
`;

const TableCellText = React.forwardRef(({ text, isOverflowing, onTextClick, cellContext }, ref) => {
    const TextComponent = onTextClick ? TableCellTextClickable : TableCellTextBase;
    const textProps = onTextClick
        ? {
              onClick: (e) => {
                  e.stopPropagation();
                  onTextClick(cellContext);
              },
          }
        : {};

    const content = (
        <TextComponent ref={ref} {...textProps}>
            {text}
        </TextComponent>
    );

    return isOverflowing ? <Tooltip message={text}>{content}</Tooltip> : content;
});

const Td = ({ cell }) => {
    const { isDragging, setNodeRef, transform } = useSortable({
        id: cell.column.id,
    });

    const textRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        if (textRef.current) {
            const scrollHeight = textRef.current.scrollHeight;
            const offsetHeight = textRef.current.offsetHeight;
            const newOverflowing = scrollHeight > offsetHeight;
            setIsOverflowing((prev) => (prev !== newOverflowing ? newOverflowing : prev));
        }
    }, []);

    const style = {
        opacity: isDragging ? 0.8 : 1,
        padding: "8px 6px",
        height: "48px",
        transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
        transition: "width transform 0.2s ease-in-out",
        zIndex: isDragging ? 1 : 0,
        color: "var(--color-SubBlack)",
    };

    const text = cell.column.columnDef.text ? cell.column.columnDef.text(cell.getContext()) : "";
    const content = cell.column.columnDef.content ? cell.column.columnDef.content(cell.getContext()) : "";
    const onTextClick = cell.column.columnDef.onTextClick;

    return (
        <TableCell
            key={cell.id}
            ref={setNodeRef}
            style={{
                ...style,
                width: cell.column.getSize(),
            }}
        >
            <TableCellWrap>
                <TableCellText
                    ref={textRef}
                    text={text}
                    isOverflowing={isOverflowing}
                    onTextClick={onTextClick}
                    cellContext={cell.getContext()}
                />
                <TableCellContent>{content}</TableCellContent>
            </TableCellWrap>
        </TableCell>
    );
};

export default Td;
